import { useState } from "react"
import { Ban, X } from "lucide-react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { Button } from "@/clientWeb/components/ui/button"
import { AvatarPhoto } from "./avatar-photo"

interface BlockedContact {
  id: string
  name: string
  imageUrl?: string
  avatarColor?: string
}

interface BlockedContactsPageProps {
  blockedContacts: BlockedContact[]
  onUnblock: (id: string) => void
  onClose?: () => void
}

export function BlockedContactsPage({ blockedContacts, onUnblock, onClose }: BlockedContactsPageProps) {
  const t = useTranslation()
  const [pendingUnblock, setPendingUnblock] = useState<BlockedContact | null>(null)
  return (
    <div className="flex flex-col h-full bg-card overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border shrink-0">
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        )}
        <h2 className="text-base font-semibold text-foreground">{t("presence.blockedContacts")}</h2>
      </div>

      {/* Blocked contacts list */}
      <div className="flex-1 overflow-y-auto">
        {blockedContacts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-8 py-16">
            <Ban className="w-12 h-12 text-muted-foreground/30 mb-4" />
            <p className="text-sm text-muted-foreground">{t("presence.noBlockedContacts")}</p>
          </div>
        ) : (
          blockedContacts.map(contact => (
            <div
              key={contact.id}
              className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors border-b border-border/50"
            >
              <div className="w-12 h-12 rounded-full bg-muted overflow-hidden shrink-0">
                <AvatarPhoto
                  src={contact.imageUrl}
                  alt={contact.name}
                  className="w-full h-full object-cover"
                  fallback={
                    <div
                      className="w-full h-full flex items-center justify-center text-sm font-semibold text-muted-foreground bg-primary/10 text-primary"
                      style={{ backgroundColor: contact.avatarColor }}
                    >
                      {contact.name[0]?.toUpperCase() || "?"}
                    </div>
                  }
                  contactId={contact.id}
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground truncate">{contact.name}</p>
                <p className="text-xs text-muted-foreground">{t("presence.blocked")}</p>
              </div>
              <button
                onClick={() => setPendingUnblock(contact)}
                className="px-3 py-1.5 text-xs font-medium text-primary bg-primary/10 rounded-lg hover:bg-primary/20 transition-colors"
              >
                {t("presence.unblock")}
              </button>
            </div>
          ))
        )}
      </div>

      {/* Unblock Confirmation Dialog */}
      {pendingUnblock && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setPendingUnblock(null)} />
          <div className="relative bg-card rounded-2xl shadow-2xl border border-border p-6 w-full max-w-sm">
            <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto mb-4">
              <Ban className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
            <h3 className="text-lg font-semibold text-foreground text-center mb-1">Unblock {pendingUnblock.name}?</h3>
            <p className="text-sm text-muted-foreground text-center mb-6">
              They will be able to send you messages and see your status again.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setPendingUnblock(null)}>
                {t("common.cancel")}
              </Button>
              <Button
                className="flex-1"
                onClick={() => { onUnblock(pendingUnblock.id); setPendingUnblock(null) }}
              >
                {t("presence.unblock")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
